// ══════════════════════════════════════════════════════════════
// COMPASS – Fund Effects
// ══════════════════════════════════════════════════════════════
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { FundActions } from './fund.actions';
import { ToastActions } from '../toast/toast.actions';
import { ApiConfiguration } from '../../api/api-configuration';
import { getAllByManagementCompanyId } from '../../api/fn/fund/get-all-by-management-company-id';
import { getById1 } from '../../api/fn/fund/get-by-id-1';
import { create2 } from '../../api/fn/fund/create-2';
import { update1 } from '../../api/fn/fund/update-1';
import { delete1 } from '../../api/fn/fund/delete-1';
import { FundDto } from '../../api/models/fund-dto';
import { errorMessage, readApiError } from '../../utils/api-error.utils';

@Injectable()
export class FundEffects {
  private readonly actions$ = inject(Actions);
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject(ApiConfiguration);
  private readonly router = inject(Router);

  readonly loadFunds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FundActions.loadFunds),
      switchMap(({ managementCompanyId }) =>
        getAllByManagementCompanyId(this.http, this.apiConfig.rootUrl, { managementCompanyId }).pipe(
          map((response) => {
            const body = response.body;
            const funds: FundDto[] = (body as unknown as FundDto[]) ?? [];
            return FundActions.loadFundsSuccess({ funds });
          }),
          catchError((error) =>
            [FundActions.loadFundsFailure({ error: errorMessage(readApiError(error), 'MANAGER.FUNDS.ERROR.LOAD_FUNDS') })]
          )
        )
      )
    )
  );

  readonly loadFund$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FundActions.loadFund),
      switchMap(({ fundId }) =>
        getById1(this.http, this.apiConfig.rootUrl, { id: fundId }).pipe(
          map((response) => FundActions.loadFundSuccess({ fund: response.body })),
          catchError((error) =>
            [FundActions.loadFundFailure({ error: errorMessage(readApiError(error), 'MANAGER.FUNDS.ERROR.LOAD_FUND') })]
          )
        )
      )
    )
  );

  readonly createFund$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FundActions.createFund),
      switchMap(({ fund, managementCompanyId }) =>
        create2(this.http, this.apiConfig.rootUrl, { body: fund }).pipe(
          switchMap((response) => [
            FundActions.createFundSuccess({ fund: response.body, managementCompanyId }),
            ToastActions.showToast({
              message: 'MANAGER.FUNDS.CREATE_SUCCESS',
              notificationType: 'success',
            }),
          ]),
          catchError((error) =>
            [FundActions.createFundFailure({ error: errorMessage(readApiError(error), 'MANAGER.FUNDS.ERROR.CREATE') })]
          )
        )
      )
    )
  );

  readonly createFundSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FundActions.createFundSuccess),
        tap(({ managementCompanyId }) => {
          this.router.navigate(['/company', managementCompanyId, 'funds']);
        })
      ),
    { dispatch: false }
  );

  readonly updateFund$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FundActions.updateFund),
      switchMap(({ fund }) =>
        update1(this.http, this.apiConfig.rootUrl, { id: fund.id!, body: fund }).pipe(
          switchMap((response) => [
            FundActions.updateFundSuccess({ fund: response.body }),
            ToastActions.showToast({
              message: 'MANAGER.FUNDS.UPDATE_SUCCESS',
              notificationType: 'success',
            }),
          ]),
          catchError((error) =>
            [FundActions.updateFundFailure({ error: errorMessage(readApiError(error), 'MANAGER.FUNDS.ERROR.UPDATE') })]
          )
        )
      )
    )
  );

  readonly updateFundSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FundActions.updateFundSuccess),
        tap(({ fund }) => {
          const companyId = fund.managementCompanyId;
          if (companyId) {
            this.router.navigate(['/company', companyId, 'funds']);
          }
        })
      ),
    { dispatch: false }
  );

  readonly deleteFund$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FundActions.deleteFund),
      switchMap(({ fundId }) =>
        delete1(this.http, this.apiConfig.rootUrl, { id: fundId }).pipe(
          switchMap(() => [
            FundActions.deleteFundSuccess({ fundId }),
            ToastActions.showToast({
              message: 'MANAGER.FUNDS.DELETE_SUCCESS',
              notificationType: 'success',
            }),
          ]),
          catchError((error) =>
            [FundActions.deleteFundFailure({ error: errorMessage(readApiError(error), 'MANAGER.FUNDS.ERROR.DELETE') })]
          )
        )
      )
    )
  );

  readonly handleFailures$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        FundActions.loadFundsFailure,
        FundActions.loadFundFailure,
        FundActions.createFundFailure,
        FundActions.updateFundFailure,
        FundActions.deleteFundFailure,
      ),
      map(({ error }) =>
        ToastActions.showToast({
          message: error,
          notificationType: 'error',
        })
      )
    )
  );
}
